/**
 * Provisioning Script Bundle Types
 *
 * Portable JSON bundle for exporting / importing a provisioning script
 * (metadata + inlined step bodies). Mirrors the campaign-config bundle
 * (`CompleteCampaignConfig`) pattern: a client-orchestrated bundle that
 * does NOT live behind a dedicated server route — `exportScript` and
 * `importScript` in the admin dashboard fan out across the existing
 * granular endpoints.
 *
 * NOTE: The authoritative server-side script schema lives in
 * `apps/realtime-api/src/services/shared/mongodb/schemas/provisioningScript.ts`.
 * The schema here intentionally only covers the fields needed to
 * re-create a script (name / description / steps / variables / timeout)
 * plus a small `_meta` block for provenance. Status, review history,
 * archived flag, etc. are NOT included — imports always create a fresh
 * `draft` script with a new ID.
 *
 * Step bodies are inlined as `content` (rather than referencing S3 keys)
 * so the bundle is fully self-contained across environments / buckets.
 * `sourceContentKey` is informational metadata only and is ignored on
 * import.
 */

import { z } from 'zod';

/**
 * Canonical bundle-schema version. Bump (and add a migration path) on
 * any breaking change to `completeProvisioningScriptSchema`. The zod
 * schema below derives its `schemaVersion` literal from this constant
 * so the value is defined in exactly one place.
 */
export const PROVISIONING_SCRIPT_BUNDLE_SCHEMA_VERSION = '1' as const;

/**
 * Operator-variable definition mirrored from the realtime-api script
 * schema. Kept narrow on purpose — Messaging does not need to know
 * about the full script document.
 */
export const provisioningOperatorVariableDefinitionSchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  defaultValue: z.string().optional(),
});

export type ProvisioningOperatorVariableDefinition = z.infer<
  typeof provisioningOperatorVariableDefinitionSchema
>;

/**
 * A single step inside an exported bundle. `content` is the inlined
 * PowerShell body (materialised from S3 at export time). The optional
 * `sourceContentKey` is provenance metadata only — it is NOT consumed
 * on import (the importer creates fresh S3 keys via the normal
 * createScript path).
 */
export const provisioningScriptBundleStepSchema = z.object({
  name: z.string().min(1),
  content: z.string(),
  timeoutSeconds: z.number().int().min(30).max(14400).optional(),
  sourceContentKey: z.string().optional(),
});

export type ProvisioningScriptBundleStep = z.infer<typeof provisioningScriptBundleStepSchema>;

/**
 * Complete provisioning-script bundle. Versioned (`schemaVersion`) so
 * future field additions can be detected and migrated client-side.
 *
 * `sourceScriptId` is provenance only — `importScript` always creates
 * a NEW script with a new ID rather than overwriting the source.
 */
export const completeProvisioningScriptSchema = z.object({
  schemaVersion: z.literal(PROVISIONING_SCRIPT_BUNDLE_SCHEMA_VERSION),
  exportedAt: z.string().min(1),
  sourceScriptId: z.string().min(1),

  name: z.string().min(1),
  description: z.string().default(''),
  type: z.literal('powershell').default('powershell'),
  timeoutSeconds: z.number().int().min(30).max(14400).optional(),
  operatorVariableDefinitions: z
    .array(provisioningOperatorVariableDefinitionSchema)
    .default([]),
  steps: z.array(provisioningScriptBundleStepSchema).min(1),
});

export type CompleteProvisioningScript = z.infer<typeof completeProvisioningScriptSchema>;
